/**
 * ModuleReportTab Component
 * 
 * Shows generated report with PDF export functionality.
 * Reports are persisted in the database and loaded on mount.
 * Generation is triggered from ModuleResultsTab.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, 
  Download, 
  RefreshCw, 
  CheckCircle, 
  Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import { normalizeTraceability, type DataReference } from '@/lib/modules/traceability-utils';
import { sanitizeReportHtml } from '@/lib/utils/sanitize-html';

interface Finding {
  id: string;
  finding_type: string;
  severity: string;
  description: string;
  created_at: string;
  data_reference?: DataReference & {
    details?: string;
    period?: string;
    amount?: number;
    recommendation?: string;
    score?: number;
  };
}

interface ModuleReportTabProps {
  auditId: string;
  moduleId: string;
  findings: Finding[];
  hasCompletedAnalysis: boolean;
  onNavigateToRun: () => void;
  // Optional: for controlled generation from parent
  onReportGenerated?: () => void;
}

export function ModuleReportTab({
  auditId,
  moduleId,
  findings,
  hasCompletedAnalysis,
  onNavigateToRun,
  onReportGenerated,
}: ModuleReportTabProps) {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState<string | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(true);
  const [latestRevisionId, setLatestRevisionId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string | null>(null);

  // Fetch existing report on mount
  const fetchExistingReport = useCallback(async () => {
    if (!auditId || !moduleId) return;
    
    setIsLoadingReport(true);
    try {
      // Get latest revision for this module
      // Get all module aliases for backward compatibility
      const { getModuleAliases } = await import('@/lib/modules')
      const moduleAliases = getModuleAliases(moduleId)
      const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',')
      
      const { data: revisions } = await supabase
        .from('revisions')
        .select('id, company_name')
        .eq('audit_id', auditId)
        .or(moduleTypeFilter)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);

      if (!revisions || revisions.length === 0) {
        setIsLoadingReport(false);
        return;
      }

      const revisionId = revisions[0].id;
      setLatestRevisionId(revisionId);
      setCompanyName(revisions[0].company_name || null);

      // Check for existing report
      const { data: reportData } = await supabase
        .from('revision_reports')
        .select('report_html, executive_summary, created_at')
        .eq('revision_id', revisionId)
        .maybeSingle();

      if (reportData) {
        setReport(reportData.report_html || reportData.executive_summary || null);
        setReportGeneratedAt(reportData.created_at);
      }

      // Fetch org settings for PDF export (logo + primary color)
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: orgSettings } = await supabase
          .from('org_settings')
          .select('logo_url, report_primary_color')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (orgSettings?.logo_url) {
          setLogoUrl(orgSettings.logo_url);
        }
        if (orgSettings?.report_primary_color) {
          setPrimaryColor(orgSettings.report_primary_color);
        }
      }
    } catch (error) {
      console.error('[ModuleReportTab] Error fetching report:', error);
    } finally {
      setIsLoadingReport(false);
    }
  }, [auditId, moduleId]);

  useEffect(() => {
    fetchExistingReport();
  }, [fetchExistingReport]);

  // Helper to calculate total negative days from runs
  const calculateTotalNegativeDays = (runs: any[]): number => {
    if (!runs || runs.length === 0) return 0;
    return runs.reduce((sum, r) => sum + (r.days || 0), 0);
  };

  const generateReport = async () => {
    // Allow generation even with 0 findings - the report will show summary + positive assessments

    setIsGenerating(true);
    try {
      // Get the revision ID if we don't have it
      let revisionId = latestRevisionId;
      if (!revisionId) {
        // Get all module aliases for backward compatibility
        const { getModuleAliases } = await import('@/lib/modules')
        const moduleAliases = getModuleAliases(moduleId)
        const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',')
        
        const { data: revisions } = await supabase
          .from('revisions')
          .select('id')
          .eq('audit_id', auditId)
          .or(moduleTypeFilter)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1);
        
        revisionId = revisions?.[0]?.id || null;
        if (revisionId) {
          setLatestRevisionId(revisionId);
        }
      }

      // Fetch rich data_summary from the latest batch
      let batchDataSummary: any = null;
      let aggregateDataSummary: any = null;
      let positiveAssessments: any[] = [];
      
      if (revisionId) {
        // Fetch from batches
        const { data: batches } = await supabase
          .from('batches')
          .select('result_json')
          .eq('revision_id', revisionId)
          .eq('state', 'completed')
          .order('created_at', { ascending: false })
          .limit(1);
        
        const resultJson = batches?.[0]?.result_json as any;
        batchDataSummary = resultJson?.data_summary || null;
        
        // Fetch from aggregates (for positive_assessments)
        const { data: aggregates } = await supabase
          .from('aggregates')
          .select('summary_json')
          .eq('revision_id', revisionId)
          .maybeSingle();
        
        if (aggregates?.summary_json) {
          const summaryJson = aggregates.summary_json as any;
          aggregateDataSummary = summaryJson.data_summary || null;
          positiveAssessments = summaryJson.positive_assessments || [];
        }
      }

      // Build rich summary with negative balance statistics
      const negativeBalanceStats = batchDataSummary ? {
        totalDays: batchDataSummary.negative_days_count || calculateTotalNegativeDays(batchDataSummary.negative_balance_runs),
        occurrences: batchDataSummary.negative_balance_runs?.length || 0,
        maxConsecutiveDays: batchDataSummary.max_consecutive_negative_days || 0,
        minNegativeBalance: batchDataSummary.min_negative_balance,
        maxNegativeBalance: batchDataSummary.max_negative_balance,
        avgNegativeBalance: batchDataSummary.avg_negative_balance,
        // Legacy fallback fields
        minBalance: batchDataSummary.min_negative_balance ?? batchDataSummary.min_balance,
        maxBalance: batchDataSummary.max_negative_balance,
        runs: batchDataSummary.negative_balance_runs || [],
      } : null;

      // Map findings with normalized traceability data
      const mappedFindings = findings.map(f => {
        const trace = normalizeTraceability(f.data_reference);
        return {
          severity: f.severity,
          category: f.finding_type,
          message: f.description,
          details: f.data_reference?.details || '',
          affectedPeriod: f.data_reference?.period || '',
          amount: f.data_reference?.amount || 0,
          score: f.data_reference?.score,
          // Use normalized traceability (canonical → affected_transactions → legacy)
          rowNumbers: trace.rowIndices,
          dates: trace.dates,
          amounts: trace.amounts,
          affectedTransactions: trace.affectedTransactions,
          positionNote: trace.positionNote,
        };
      });

      console.log('[ModuleReportTab] Mapped findings for report:', {
        totalFindings: mappedFindings.length,
        withRowNumbers: mappedFindings.filter(f => f.rowNumbers.length > 0).length,
        withDates: mappedFindings.filter(f => f.dates.length > 0).length,
        sampleFinding: mappedFindings[0],
      });

      const { data, error } = await supabase.functions.invoke('generate-module-report', {
        body: {
          findings: mappedFindings,
          summary: {
            total_findings: findings.length,
            high_risk: findings.filter(f => f.severity === 'high' || f.severity === 'critical').length,
            medium_risk: findings.filter(f => f.severity === 'medium').length,
            low_risk: findings.filter(f => f.severity === 'low').length,
            // Add rich negative balance statistics
            negativeBalanceStats,
            interestTotal: batchDataSummary?.interest_total || 0,
            lateFeeDetected: batchDataSummary?.late_fee_detected || false,
            costInterestTotal: batchDataSummary?.cost_interest_total || 0,
          },
          moduleType: moduleId,
          companyName: null,
          revisionId: revisionId,
          // Send data summary and positive assessments for 0-finding reports
          dataSummary: aggregateDataSummary || batchDataSummary,
          positiveAssessments: positiveAssessments,
        }
      });

      if (error) throw error;

      setReport(data.report);
      setReportGeneratedAt(new Date().toISOString());
      
      toast({
        title: 'Rapport genererad',
        description: data.savedToDatabase 
          ? 'Rapporten är sparad och tillgänglig för visning och export'
          : 'Rapporten är tillgänglig för visning och export',
      });

      onReportGenerated?.();
    } catch (err) {
      console.error('[ModuleReportTab] Report generation error:', err);
      toast({
        title: 'Fel vid rapportgenerering',
        description: err instanceof Error ? err.message : 'Kunde inte generera rapport',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getModuleReportTitle = (moduleId: string): string => {
    const titles: Record<string, string> = {
      'module_5': 'Modulrapport - Skattekontoanalys',
      'taxes_fees': 'Modulrapport - Skattekontoanalys',
      'vat_analysis': 'Modulrapport - Momsanalys',
      'payroll_analysis': 'Modulrapport - Löneanalys',
      'revenue_analysis': 'Modulrapport - Intäktsanalys',
      'cost_analysis': 'Modulrapport - Kostnadsanalys'
    };
    return titles[moduleId] || 'Modulrapport';
  };

  const convertMarkdownToHtml = (text: string): string => {
    if (!text) return '';
    
    // Process line by line for better list handling
    const lines = text.split('\n');
    const processedLines: string[] = [];
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Check for bullet list item
      if (trimmed.startsWith('- ')) {
        if (!inList || listType !== 'ul') {
          if (inList) processedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
          processedLines.push('<ul class="list-disc ml-6 my-2 space-y-1">');
          inList = true;
          listType = 'ul';
        }
        const content = trimmed.slice(2);
        processedLines.push(`<li class="text-foreground">${content}</li>`);
        continue;
      }
      
      // Check for numbered list item
      const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        if (!inList || listType !== 'ol') {
          if (inList) processedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
          processedLines.push('<ol class="list-decimal ml-6 my-2 space-y-1">');
          inList = true;
          listType = 'ol';
        }
        processedLines.push(`<li class="text-foreground">${numberedMatch[2]}</li>`);
        continue;
      }
      
      // Close list if we're in one and hit a non-list line
      if (inList && trimmed !== '') {
        processedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
        inList = false;
        listType = null;
      }
      
      // Check for headers (standalone bold lines as headers)
      if (trimmed.match(/^\*\*[^*]+\*\*$/)) {
        const headerText = trimmed.slice(2, -2);
        processedLines.push(`<h3 class="text-lg font-semibold mt-6 mb-3 text-primary border-b border-border pb-2">${headerText}</h3>`);
        continue;
      }
      
      // Regular line
      if (trimmed === '') {
        processedLines.push('<br/>');
      } else {
        processedLines.push(`<p class="my-1">${trimmed}</p>`);
      }
    }
    
    // Close any open list
    if (inList) {
      processedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
    }
    
    // Join and process inline formatting
    return processedLines.join('\n')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  };

  const exportPDF = async () => {
    if (!report) {
      toast({
        title: 'Ingen rapport',
        description: 'Generera en rapport först',
        variant: 'destructive',
      });
      return;
    }

    // Helper: Load image as base64 for jsPDF
    const loadImageAsBase64 = (url: string): Promise<string | null> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            } else {
              resolve(null);
            }
          } catch {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });
    };

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let yPos = margin;

    // Helper: Decode HTML entities
    const decodeHtmlEntities = (text: string): string => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    };

    // Helper: Normalize special characters for PDF (Helvetica only supports Latin-1)
    const normalizeForPdf = (text: string): string => {
      return text
        .replace(/−/g, '-')      // Unicode minus → hyphen
        .replace(/–/g, '-')      // En-dash → hyphen
        .replace(/—/g, '-')      // Em-dash → hyphen
        .replace(/"/g, '"')      // Smart quotes
        .replace(/"/g, '"')
        .replace(/'/g, "'")
        .replace(/'/g, "'")
        .replace(/…/g, '...')    // Ellipsis
        .replace(/\*\*/g, '')    // Remove markdown bold
        .replace(/\*/g, '');     // Remove markdown italic
    };

    // Parse primary color (hex to RGB)
    const parseHexColor = (hex: string | null): { r: number; g: number; b: number } => {
      if (!hex) return { r: 30, g: 64, b: 175 }; // Default blue (#1e40af)
      const cleanHex = hex.replace('#', '');
      const r = parseInt(cleanHex.substring(0, 2), 16) || 30;
      const g = parseInt(cleanHex.substring(2, 4), 16) || 64;
      const b = parseInt(cleanHex.substring(4, 6), 16) || 175;
      return { r, g, b };
    };
    
    const brandColor = parseHexColor(primaryColor);

    // Footer height reservation
    const footerHeight = 12;
    const contentBottom = pageHeight - margin - footerHeight;

    // Helper: Check for page break (accounting for footer)
    const checkPageBreak = (neededHeight: number) => {
      if (yPos + neededHeight > contentBottom) {
        doc.addPage();
        yPos = margin;
      }
    };
    
    // Helper: Set brand color for text
    const setBrandColor = () => {
      doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
    };
    
    // Helper: Reset to black text
    const resetTextColor = () => {
      doc.setTextColor(0, 0, 0);
    };

    // Header with optional logo
    const logoMaxHeight = 15;
    const logoMaxWidth = 40;
    let logoLoaded = false;

    if (logoUrl) {
      try {
        const base64Logo = await loadImageAsBase64(logoUrl);
        if (base64Logo) {
          // Calculate logo dimensions maintaining aspect ratio
          const img = new Image();
          img.src = base64Logo;
          const aspectRatio = img.width / img.height;
          let logoWidth = logoMaxWidth;
          let logoHeight = logoWidth / aspectRatio;
          
          if (logoHeight > logoMaxHeight) {
            logoHeight = logoMaxHeight;
            logoWidth = logoHeight * aspectRatio;
          }
          
          // Place logo in upper right corner
          const logoX = pageWidth - margin - logoWidth;
          const logoY = margin;
          doc.addImage(base64Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
          logoLoaded = true;
        }
      } catch (error) {
        console.warn('[PDF Export] Could not load logo:', error);
      }
    }

    // Title with brand color
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    setBrandColor();
    const reportTitle = getModuleReportTitle(moduleId);
    doc.text(reportTitle.toUpperCase(), margin, yPos);
    resetTextColor();
    yPos += 10;

    // Company name under title (if available)
    if (companyName) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(companyName, margin, yPos);
      resetTextColor();
      yPos += 8;
    }

    // Date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`Genererad: ${new Date().toLocaleDateString('sv-SE')}`, margin, yPos);
    resetTextColor();
    
    // If logo was loaded, make sure we have enough space
    if (logoLoaded) {
      yPos = Math.max(yPos + 10, margin + logoMaxHeight + 4);
    } else {
      yPos += 10;
    }

    // Horizontal line under header with brand color
    doc.setDrawColor(brandColor.r, brandColor.g, brandColor.b);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    doc.setLineWidth(0.2);
    yPos += 10;

    // Process report line by line
    const lines = report.split('\n');
    
    for (const rawLine of lines) {
      // Decode HTML entities and normalize characters
      let line = normalizeForPdf(decodeHtmlEntities(rawLine.trim()));
      
      // Skip empty lines but add some spacing
      if (line === '') {
        yPos += 3;
        continue;
      }

      // Detect headers (# syntax)
      const h1Match = line.match(/^#\s+(.+)$/);
      const h2Match = line.match(/^##\s+(.+)$/);
      const h3Match = line.match(/^###\s+(.+)$/);

      // H1 - Large heading with brand color and underline
      if (h1Match) {
        checkPageBreak(18);
        yPos += 8; // Extra space before h1
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        setBrandColor();
        doc.text(h1Match[1], margin, yPos);
        // Subtle underline
        doc.setDrawColor(brandColor.r, brandColor.g, brandColor.b);
        doc.setLineWidth(0.3);
        doc.line(margin, yPos + 2, margin + doc.getTextWidth(h1Match[1]), yPos + 2);
        doc.setLineWidth(0.2);
        resetTextColor();
        yPos += 12;
        continue;
      }

      // H2 - Medium heading with brand color
      if (h2Match) {
        checkPageBreak(14);
        yPos += 6;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        setBrandColor();
        doc.text(h2Match[1], margin, yPos);
        resetTextColor();
        yPos += 10;
        continue;
      }

      // H3 - Small heading, slightly darker gray
      if (h3Match) {
        checkPageBreak(12);
        yPos += 4;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text(h3Match[1], margin, yPos);
        resetTextColor();
        yPos += 8;
        continue;
      }

      // Bullet list item with brand-colored bullet
      if (line.startsWith('- ')) {
        checkPageBreak(8);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        const bulletText = line.slice(2);
        const wrappedLines = doc.splitTextToSize(bulletText, contentWidth - 10);
        wrappedLines.forEach((wrappedLine: string, idx: number) => {
          checkPageBreak(6);
          if (idx === 0) {
            setBrandColor();
            doc.text('\u2022', margin + 3, yPos);
            resetTextColor();
          }
          doc.text(wrappedLine, margin + 10, yPos);
          yPos += 6;
        });
        continue;
      }

      // Numbered list item with brand-colored number
      const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        checkPageBreak(8);
        doc.setFontSize(11);
        const numText = numberedMatch[2];
        const wrappedLines = doc.splitTextToSize(numText, contentWidth - 12);
        wrappedLines.forEach((wrappedLine: string, idx: number) => {
          checkPageBreak(6);
          if (idx === 0) {
            doc.setFont('helvetica', 'bold');
            setBrandColor();
            doc.text(`${numberedMatch[1]}.`, margin, yPos);
            resetTextColor();
            doc.setFont('helvetica', 'normal');
          }
          doc.text(wrappedLine, margin + 12, yPos);
          yPos += 6;
        });
        continue;
      }

      // Regular paragraph
      checkPageBreak(8);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const wrappedLines = doc.splitTextToSize(line, contentWidth);
      wrappedLines.forEach((wrappedLine: string) => {
        checkPageBreak(6);
        doc.text(wrappedLine, margin, yPos);
        yPos += 6;
      });
    }

    // Add footer to all pages with brand color accents
    const totalPages = doc.getNumberOfPages();
    const footerY = pageHeight - margin;
    
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Footer line with brand color
      doc.setDrawColor(brandColor.r, brandColor.g, brandColor.b);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 6, pageWidth - margin, footerY - 6);
      doc.setLineWidth(0.2);
      
      // "Powered by AuditAI" on the left (brand color)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(brandColor.r, brandColor.g, brandColor.b);
      doc.text('Powered by AuditAI.tech', margin, footerY);
      
      // Page number on the right (gray)
      doc.setTextColor(120, 120, 120);
      const pageText = `Sida ${i} av ${totalPages}`;
      const pageTextWidth = doc.getTextWidth(pageText);
      doc.text(pageText, pageWidth - margin - pageTextWidth, footerY);
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
    }

    doc.save(`${reportTitle.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
    
    toast({
      title: 'PDF exporterad',
      description: 'Rapporten har laddats ner',
    });
  };

  // No analysis completed yet - but 0 findings is OK (clean report)
  if (!hasCompletedAnalysis) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="p-4 rounded-full bg-muted">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">Ingen rapport tillgänglig</p>
              <p className="text-sm text-muted-foreground mt-1">
                Kör en analys först för att kunna generera en rapport
              </p>
            </div>
            <Button onClick={onNavigateToRun} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Gå till Kör-fliken
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (isLoadingReport) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Laddar rapport...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Report Actions Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Analysrapport
              </CardTitle>
              <CardDescription>
                {report 
                  ? `Genererad ${reportGeneratedAt ? new Date(reportGeneratedAt).toLocaleDateString('sv-SE') : ''}`
                  : findings.length > 0 
                    ? `${findings.length} fynd tillgängliga för rapport`
                    : 'Ren analys - generera sammanfattningsrapport'
                }
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {report && (
                <Badge variant="secondary" className="gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Rapport sparad
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            {report ? (
              <>
                <Button onClick={exportPDF} variant="default" className="gap-2">
                  <Download className="h-4 w-4" />
                  Exportera PDF
                </Button>
                <Button 
                  onClick={generateReport} 
                  disabled={isGenerating}
                  variant="outline"
                  className="gap-2"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Genererar...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Generera ny rapport
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button 
                onClick={generateReport} 
                disabled={isGenerating}
                variant="default"
                className="gap-2"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Genererar...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generera rapport
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Preview */}
      {report && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{getModuleReportTitle(moduleId)}</CardTitle>
          </CardHeader>
        <CardContent>
          <div 
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeReportHtml(convertMarkdownToHtml(report)) }}
          />
        </CardContent>
        </Card>
      )}

      {/* Empty state when no report yet - now handled by the generate button above */}
    </div>
  );
}
